import { useState, useRef, useEffect } from 'react';
import { Check, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EditableChipProps {
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number';
  prefix?: string;
  suffix?: string;
  label?: string;
  className?: string;
  placeholder?: string;
}

export const EditableChip = ({
  value,
  onChange,
  type = 'text',
  prefix,
  suffix,
  label,
  className,
  placeholder = '',
}: EditableChipProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed) {
      onChange(type === 'number' ? String(parseFloat(trimmed) || value) : trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="inline-flex items-center gap-1.5">
        {prefix && <span className="text-muted-foreground">{prefix}</span>}
        <input
          ref={inputRef}
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={cn(
            'bg-background border border-primary rounded-lg px-3 py-1.5',
            'focus:outline-none focus:ring-2 focus:ring-primary/50',
            'min-w-[80px] max-w-[200px]',
            type === 'number' && 'w-24 text-right',
            className
          )}
          placeholder={placeholder}
          aria-label={label}
          step={type === 'number' ? '0.01' : undefined}
        />
        {suffix && <span className="text-muted-foreground">{suffix}</span>}
        <button
          onClick={handleSave}
          className="p-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          aria-label="Save"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
        'border border-dashed border-muted-foreground/40 hover:border-primary/60',
        'bg-muted/30 hover:bg-muted/50 transition-all',
        'cursor-pointer group min-h-[36px] touch-manipulation',
        className
      )}
      aria-label={label ? `Edit ${label}` : 'Edit'}
    >
      {prefix && <span className="text-muted-foreground">{prefix}</span>}
      <span>{value}</span>
      {suffix && <span className="text-muted-foreground">{suffix}</span>}
      <Pencil className="w-3 h-3 text-muted-foreground group-hover:text-primary opacity-60 group-hover:opacity-100 transition-opacity ml-1" />
    </button>
  );
};
